import { filterByExternalResource, filterByInternalResource } from 'util/filterByResource'
import { ANY_RESOURCE_ID, ANY_INTERNAL_RESOURCE_ID, ANY_EXTERNAL_RESOURCE_ID } from '../../../constants'
import { DayAvailability, Resource, TimeBlock } from '../../../types'

const blockToKey = ({ date_start, date_end }: TimeBlock) => `${date_start.getTime()}:${date_end.getTime()}`

const sortTimeBlockByDateStartAscending = (a: TimeBlock, b: TimeBlock) => {
  const aKey = blockToKey(a)
  const bKey = blockToKey(b)

  if (aKey > bKey) {
    return 1
  }

  if (aKey < bKey) {
    return -1
  }

  return 0
}

const appendAllResourcesDayAvailability = (dayAvailability: DayAvailability, resources?: Resource[]): DayAvailability => {
  const internalResourceIds = resources ? new Set(resources.filter(filterByInternalResource).map((r) => r.id)) : undefined
  const externalResourceIds = resources ? new Set(resources.filter(filterByExternalResource).map((r) => r.id)) : undefined

  const newDayAvailability = Object.entries(dayAvailability).reduce((acc, [dayKey, resourceBlocks]) => {
    const allBlocks = collectUniqueBlocks(resourceBlocks)

    acc[dayKey] = {
      [ANY_RESOURCE_ID]: allBlocks,
      ...(internalResourceIds?.size ? { [ANY_INTERNAL_RESOURCE_ID]: collectUniqueBlocks(resourceBlocks, internalResourceIds) } : {}),
      ...(externalResourceIds?.size ? { [ANY_EXTERNAL_RESOURCE_ID]: collectUniqueBlocks(resourceBlocks, externalResourceIds) } : {}),
      ...dayAvailability[dayKey],
    }

    return acc
  }, {} as Record<string, Record<string, TimeBlock[]>>)

  return newDayAvailability
}

/**
 * Collects unique time blocks from the given resource blocks, optionally filtering by specific resource IDs.
 * @param resourceBlocks - A mapping of resource IDs to their corresponding time blocks
 * @param resourceIds - An optional set of resource IDs to filter the blocks by
 * @returns An array of unique time blocks, sorted by their start date
 */
function collectUniqueBlocks(resourceBlocks: Record<string, TimeBlock[]>, resourceIds?: Set<string>): TimeBlock[] {
  const uniqueBlocks = new Map<string, TimeBlock>()

  Object.entries(resourceBlocks).forEach(([resourceId, blocks]) => {
    if (resourceIds && !resourceIds.has(resourceId)) return
    blocks.forEach((block) => {
      const key = blockToKey(block)
      if (!uniqueBlocks.get(key)) {
        uniqueBlocks.set(key, block)
      }
    })
  })

  return [...uniqueBlocks.values()].sort(sortTimeBlockByDateStartAscending)
}

export { appendAllResourcesDayAvailability }
